import { useCallback, useEffect, useRef, useState } from 'react';
import { ajaxPostToCelery, type CeleryWait } from '../celeryRoundTrip';

/**
 * Shared celery round-trip chassis for the Costing / Pricing tabs (§B2/§B3 —
 * the Sales tab's pre_render pattern extracted): POST `{project_id}` to the
 * report endpoint, wait for the socket result (emit/backoff re-ask via
 * celeryRoundTrip.ts), expose `{pending, generateId, data}` + `recalc`.
 *
 * - The round trip runs ONCE when `ready` first turns true (the per-tab boot
 *   order gate: costing fetches its collection FIRST, pricing fires celery
 *   first), and again on every `recalc()` (RECALC button).
 * - On an env without a REACHABLE socket server (e.g. the :8005 verify server,
 *   whose page port serves no socket.io), the promise never settles and the
 *   tab keeps its dotted preloader forever — the FAITHFUL legacy state on a
 *   socketless env. dev3 itself has a working websocket server (verified
 *   2026-07-11), so this only bites alternate ports/local Vite dev.
 * - A failed POST also leaves the preloader up (legacy parity — the .then
 *   never runs).
 */
export interface CeleryReport<T> {
  pending: boolean;
  generateId: string | null;
  data: T | null;
  recalc: () => void;
}

export function useCeleryReport<T>(
  url: string,
  projectId: number,
  ready: boolean,
  dataKey: string,
  /** Called when a run's result arrives: `'opened'` for the boot run when the
   * tab opens, `'recalc'` for a RECALC. Optional; analytics uses it. */
  onDone?: (trigger: 'opened' | 'recalc') => void
): CeleryReport<T> {
  const [state, setState] = useState<{
    pending: boolean;
    generateId: string | null;
    data: T | null;
  }>({ pending: true, generateId: null, data: null });
  const waitRef = useRef<CeleryWait | null>(null);
  const bootedRef = useRef(false);
  const onDoneRef = useRef(onDone);
  useEffect(() => {
    onDoneRef.current = onDone;
  });

  const run = useCallback((trigger: 'opened' | 'recalc') => {
    waitRef.current?.cancel();
    setState((s) => ({ ...s, pending: true }));
    const wait = ajaxPostToCelery(url, { project_id: projectId });
    waitRef.current = wait;
    wait.promise
      .then((msg) => {
        const data = (msg.data ?? {}) as Record<string, unknown>;
        setState({
          pending: false,
          generateId: (data.generate_id as string | undefined) ?? null,
          data: (data[dataKey] as T | undefined) ?? null,
        });
        onDoneRef.current?.(trigger);
      })
      .catch(() => undefined); // failed POST: preloader stays (legacy parity)
  }, [url, projectId, dataKey]);
  const recalc = useCallback(() => run('recalc'), [run]);

  useEffect(() => {
    if (ready && !bootedRef.current) {
      bootedRef.current = true;
      run('opened');
    }
  }, [ready, run]);
  useEffect(() => () => waitRef.current?.cancel(), []);

  return { pending: state.pending, generateId: state.generateId, data: state.data, recalc };
}
